import { useEffect, useRef } from "react";
import {
  getChangedFields,
  getPendingLeave,
  hasDirtyFlag,
  requestLeave,
  subscribe,
} from "shared/helpers/unsavedChanges.store";

/**
 * Block the browser Back / Forward buttons while unsaved changes remain (KP-2453).
 *
 * `popstate` only fires AFTER the URL has already changed and cannot be cancelled, so this
 * uses a sentinel: as soon as a form turns dirty an extra history entry pointing at the
 * current URL is pushed. The first Back press merely consumes that spare entry — the URL is
 * unchanged and the page never leaves — which gives us the chance to raise the popup. The
 * sentinel is pushed again inside the handler so pressing Back once more, while the popup is
 * open, still cannot escape.
 *
 * When the form is clean but a sentinel is still pending, the next Back press adds its own
 * `go(-1)` to consume the spare entry, so one Back press still means one step back.
 *
 * Caveat: this only works while Back stays inside the same document. If the page before this one
 * was a separate document (typed URL, full reload, external link), Back leaves the document and
 * the browser fires `beforeunload` instead of `popstate` — the app popup cannot be shown there.
 */
const usePopStateGuard = () => {
  const hasSentinelRef = useRef(false);

  useEffect(() => {
    const pushSentinel = () => {
      window.history.pushState(null, "", window.location.href);
      hasSentinelRef.current = true;
    };

    // Place the sentinel as soon as the first form turns dirty.
    const syncSentinel = () => {
      if (hasDirtyFlag() && !hasSentinelRef.current) pushSentinel();
    };

    const handlePopState = () => {
      // A prompt is already on screen — put the sentinel straight back and stop. Never re-read the
      // diff here: a form reloading its data reports no changes for a moment, and letting Back
      // through on that reading would walk past the entry the prompt is guarding.
      if (getPendingLeave()) {
        pushSentinel();
        return;
      }

      if (getChangedFields().length === 0) {
        // The sentinel was consumed but there is nothing to warn about, so finish the step back the user expected.
        if (hasSentinelRef.current) {
          hasSentinelRef.current = false;
          window.history.go(-1);
        }
        return;
      }

      pushSentinel();
      requestLeave(() => {
        hasSentinelRef.current = false;
        // We are sitting on the sentinel, so it takes two steps back to reach the previous page.
        window.history.go(-2);
      });
    };

    syncSentinel();
    const unsubscribe = subscribe(syncSentinel);
    window.addEventListener("popstate", handlePopState);

    return () => {
      unsubscribe();
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);
};

export default usePopStateGuard;
